import { useEffect, useMemo, useState, type ReactNode } from "react";
import classnames from "classnames";

import { useTranslation } from "@/lib/i18n";
import {
  resolveThresholdBandColor,
  resolveThresholdPointColor,
} from "@/components/graph/threshold-colors";
import type { ParameterTimeSeriesThreshold } from "@/lib/api";

import {
  buildThresholdsFromRows,
  computeThresholdGaugeSegments,
  deriveThresholdRows,
  type ThresholdRowState,
} from "./time-series-thresholds-rows";
import { TimeSeriesThresholdsGauge } from "./time-series-thresholds-gauge";
import styles from "./time-series-thresholds-editor.module.css";

interface TimeSeriesThresholdsEditorProps {
  thresholds: ParameterTimeSeriesThreshold[];
  onChange: (thresholds: ParameterTimeSeriesThreshold[]) => void;
  /** Unit symbol shown next to the value input for context. */
  unitSymbol?: string;
}

const I18N_BASE = "admin.widgets.fields.model_map.time_series.thresholds";

/**
 * Fixed-row threshold editor: four pre-enumerated rows (red/orange x above/below)
 * with a toggle and value each, a central "blank" divider, and a vertical gauge
 * that visualizes the resulting regions. The persisted `thresholds` array is a
 * derived view — every edit rebuilds it in canonical order.
 *
 * Rendered as a compact "dense" line-paper layout: all rows share one box, a
 * squiggle fills the neutral divider between the above/below groups.
 */
export function TimeSeriesThresholdsEditor({
  thresholds,
  onChange,
  unitSymbol,
}: TimeSeriesThresholdsEditorProps) {
  const { t } = useTranslation();

  const rows = useMemo(() => deriveThresholdRows(thresholds), [thresholds]);
  const segments = useMemo(() => computeThresholdGaugeSegments(rows), [rows]);

  // The form array only stores active thresholds, so toggling a row off would
  // otherwise wipe its value. Keep a per-row draft of the last finite value so
  // toggling back on restores it; the form stays the source of truth for what is
  // active/saved.
  const [valueDrafts, setValueDrafts] = useState<Record<string, number>>({});

  useEffect(() => {
    setValueDrafts((previous) => {
      let next = previous;
      for (const row of rows) {
        if (row.active && Number.isFinite(row.value) && next[row.id] !== row.value) {
          if (next === previous) next = { ...previous };
          next[row.id] = row.value;
        }
      }
      return next;
    });
  }, [rows]);

  const commitRows = (nextRows: ThresholdRowState[]) => {
    onChange(buildThresholdsFromRows(nextRows));
  };

  const toggleRow = (id: string, active: boolean) => {
    commitRows(
      rows.map((row) =>
        row.id === id
          ? { ...row, active, value: active ? (valueDrafts[id] ?? Number.NaN) : row.value }
          : row,
      ),
    );
  };

  const changeValue = (id: string, value: number, follow: boolean, isEmpty: boolean) => {
    // `follow` (dense layout): the row's active state tracks the input content —
    // any non-empty value turns it on, clearing it to empty turns it off. When
    // emptied we also drop the remembered draft so the box stays blank instead of
    // snapping back to the last value.
    if (follow) {
      if (isEmpty) {
        setValueDrafts((previous) => {
          if (!(id in previous)) return previous;
          const next = { ...previous };
          delete next[id];
          return next;
        });
        commitRows(
          rows.map((row) => (row.id === id ? { ...row, value, active: false } : row)),
        );
        return;
      }
      if (Number.isFinite(value)) {
        setValueDrafts((previous) => ({ ...previous, [id]: value }));
      }
      commitRows(
        rows.map((row) => (row.id === id ? { ...row, value, active: true } : row)),
      );
      return;
    }
    if (Number.isFinite(value)) {
      setValueDrafts((previous) => ({ ...previous, [id]: value }));
    }
    commitRows(rows.map((row) => (row.id === id ? { ...row, value } : row)));
  };

  const aboveRows = rows.filter((row) => row.threshold_type === "max");
  const belowRows = rows.filter((row) => row.threshold_type === "min");

  const rowView = (row: ThresholdRowState) => {
    const colorLabel =
      row.color === "red" ? t(`${I18N_BASE}.colors.red`) : t(`${I18N_BASE}.colors.orange`);
    const directionLabel =
      row.direction === "above"
        ? t(`${I18N_BASE}.direction_above`)
        : t(`${I18N_BASE}.direction_below`);
    const hint = t(
      row.direction === "above"
        ? `${I18N_BASE}.to_positive_infinity`
        : `${I18N_BASE}.to_negative_infinity`,
    );
    // While inactive the value is gone from the form, so fall back to the
    // remembered draft to keep the last entered value visible in the disabled box.
    const displayValue = row.active ? row.value : (valueDrafts[row.id] ?? row.value);
    const toggleAria = t(`${I18N_BASE}.toggle_aria`, {
      color: colorLabel,
      direction: directionLabel,
    });
    return { colorLabel, directionLabel, hint, displayValue, toggleAria };
  };

  const valueInput = (
    row: ThresholdRowState,
    className: string,
    activateOnChange = false,
  ) => {
    const { displayValue } = rowView(row);
    // When `activateOnChange` is set the input stays enabled even for an inactive
    // row, and editing its value drives the row's active state: any non-empty
    // value turns it on, clearing it to empty turns it off. Activation is tied to
    // an actual value change (not focus) so tabbing through or accidental touches
    // do not switch a row on unintentionally.
    return (
      <input
        type="number"
        step="any"
        className={className}
        value={Number.isFinite(displayValue) ? displayValue : ""}
        disabled={!activateOnChange && !row.active}
        aria-label={t(`${I18N_BASE}.value_label`)}
        onChange={(event) =>
          changeValue(
            row.id,
            event.target.valueAsNumber,
            activateOnChange,
            event.target.value === "",
          )
        }
      />
    );
  };

  const renderDenseRow = (row: ThresholdRowState) => {
    const { toggleAria } = rowView(row);
    const symbol = row.threshold_type === "max" ? "\u2265" : "\u2264";
    const toggle = () => toggleRow(row.id, !row.active);
    return (
      // The whole row is the toggle affordance; the value field stops propagation
      // so clicking/typing there edits the value instead of flipping the row.
      <div
        key={row.id}
        role="switch"
        aria-checked={row.active}
        aria-label={toggleAria}
        tabIndex={0}
        className={classnames(
          styles.denseRow,
          styles.denseRowClickable,
          !row.active && styles.denseRowInactive,
        )}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
      >
        <span
          className={classnames(
            styles.circleToggle,
            row.active ? styles.circleOn : styles.circleOff,
          )}
          style={{
            backgroundColor: row.active
              ? resolveThresholdPointColor(row.color)
              : resolveThresholdBandColor(row.color),
            borderColor: row.active
              ? resolveThresholdPointColor(row.color)
              : resolveThresholdBandColor(row.color),
          }}
          aria-hidden
        />
        <span className={styles.denseSymbol} aria-hidden>
          {symbol}
        </span>
        <div className={styles.denseValue} onClick={(event) => event.stopPropagation()}>
          {valueInput(row, styles.denseInput, true)}
          {unitSymbol ? <span className={styles.unit}>{unitSymbol}</span> : null}
        </div>
      </div>
    );
  };

  const renderDenseBlank = (): ReactNode => (
    <div
      className={classnames(styles.denseRow, styles.denseBlank, styles.denseBlankFilled)}
      aria-hidden
    >
      <BlankSquiggle />
    </div>
  );

  const content = (
    <div className={styles.denseBox}>
      {aboveRows.map((row) => renderDenseRow(row))}
      {renderDenseBlank()}
      {belowRows.map((row) => renderDenseRow(row))}
    </div>
  );

  return (
    <div className={styles.root}>
      {content}
      <div className={styles.gaugeColumn}>
        <TimeSeriesThresholdsGauge segments={segments} unitSymbol={unitSymbol} />
      </div>
    </div>
  );
}

/** Thick hand-drawn style squiggle that stretches to fill the blank divider. */
function BlankSquiggle() {
  return (
    <svg
      className={styles.fillSquiggle}
      viewBox="0 0 120 16"
      preserveAspectRatio="none"
      role="presentation"
    >
      <path
        d="M0 8 Q 5 1 10 8 T 20 8 T 30 8 T 40 8 T 50 8 T 60 8 T 70 8 T 80 8 T 90 8 T 100 8 T 110 8 T 120 8"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
      />
    </svg>
  );
}
